import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroVibe from "@/components/hero/HeroVibe";
import { SpaCardGrid } from "@/components/spa/SpaCard";
import PremiumCarousel from "@/components/spa/PremiumCarousel";
import HomeBanner from "@/components/home/HomeBanner";
import { SpaGridSkeleton } from "@/components/states/LoadingSkeletons";
import JsonLd from "@/components/seo/JsonLd";
import api from "@/lib/api";

const POPULAR_AREAS = [
  { name: "Wakad", image: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80" },
  { name: "Baner", image: "https://images.unsplash.com/photo-1591343395082-e120087004b4?w=800&q=80" },
  { name: "Koregaon Park", image: "https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=800&q=80" },
  { name: "Hinjawadi", image: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=800&q=80" },
  { name: "Viman Nagar", image: "https://images.unsplash.com/photo-1583416750470-965b2707b355?w=800&q=80" },
  { name: "Kalyani Nagar", image: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?w=800&q=80" },
  { name: "Aundh", image: "https://images.unsplash.com/photo-1600334129128-685c5582fd35?w=800&q=80" },
  { name: "Kothrud", image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800&q=80" },
];

const POPULAR_CATEGORIES = [
  { name: "Luxury Spa", icon: "✨" },
  { name: "Ayurvedic Spa", icon: "🌿" },
  { name: "Thai Spa", icon: "🌺" },
  { name: "Couples Spa", icon: "💑" },
  { name: "Deep Tissue Massage", icon: "💐" },
  { name: "Foot Spa", icon: "🦶" },
  { name: "Wellness Spa", icon: "🕊️" },
  { name: "Aromatherapy Spa", icon: "🕯️" },
];

const TESTIMONIALS = [
  { name: "Sneha R.", rating: 5, text: "Found my favourite spa through Pune Spa Guide. The reviews were spot on and the booking process was effortless.", area: "Baner" },
  { name: "Akash V.", rating: 5, text: "Verified listings really matter. I now use this site for every wellness booking - couples package was excellent.", area: "Koregaon Park" },
  { name: "Diya M.", rating: 4, text: "Loved how I could filter by category and rating. Discovered an authentic Ayurveda spot near my office.", area: "Hinjawadi" },
];

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Pune Spa Guide",
  url: ORIGIN,
  description: "Pune & PCMC's premier directory of luxury spas, ayurvedic centres, Thai massage, and wellness retreats.",
  potentialAction: {
    "@type": "SearchAction",
    target: `${ORIGIN}/spas?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Pune Spa Guide",
  url: ORIGIN,
  logo: `${ORIGIN}/favicon.ico`,
  areaServed: { "@type": "City", name: "Pune" },
};

/** Reusable spa-grid section with header + "View all" link. */
function SpaSection({ eyebrow, title, viewAllTo, items, testId }) {
  return (
    <section data-testid={testId} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="flex items-end justify-between mb-7">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">{eyebrow}</div>
          <h2 className="font-heading text-3xl text-[#111827] mt-1">{title}</h2>
        </div>
        {viewAllTo && (
          <Link to={viewAllTo} className="text-sm flex items-center gap-1 text-[#1F4E3D] hover:underline">
            View all <ArrowRight size={14} />
          </Link>
        )}
      </div>
      {items === null ? <SpaGridSkeleton count={6} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s) => <SpaCardGrid key={s.id} spa={s} />)}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState(null);
  const [topRated, setTopRated] = useState(null);
  const [recent, setRecent] = useState(null);

  const loadHomeData = useCallback(async () => {
    const fetchSafe = (url, setter) => api.get(url).then((r) => setter(r.data.items)).catch(() => setter([]));
    await Promise.all([
      // "Hand-picked Featured Spas" surfaces PREMIUM listings only. We ask for
      // more than 6 so the client-side carousel can auto-scroll through the rest.
      fetchSafe("/spas/premium?limit=24", setFeatured),
      fetchSafe("/spas/top-rated?limit=6", setTopRated),
      fetchSafe("/spas/recent?limit=6", setRecent),
    ]);
  }, []);

  useEffect(() => {
    document.title = "Best Spa in Pune & PCMC | Pune Spa Guide";
    loadHomeData();
  }, [loadHomeData]);

  return (
    <div data-testid="home-page">
      <JsonLd data={WEBSITE_LD} />
      <JsonLd data={ORG_LD} />
      {/* HERO */}
      <HeroVibe />

      {/* FEATURED — premium listings only. If >6 spas, auto-scrolls horizontally
          every 2s. Otherwise renders as a static 3-col grid. */}
      <section data-testid="featured-spas-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-7">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Hand-picked</div>
            <h2 className="font-heading text-3xl text-[#111827] mt-1">Premium Spas</h2>
          </div>
          <Link to="/spas?premium=true" className="text-sm flex items-center gap-1 text-[#1F4E3D] hover:underline" data-testid="featured-view-all">
            View all <ArrowRight size={14} />
          </Link>
        </div>
        {featured === null ? (
          <SpaGridSkeleton count={6} />
        ) : featured.length === 0 ? (
          <div className="rounded-2xl bg-white border border-dashed border-[#E7DDCF] p-8 text-center text-sm text-[#4B5563]" data-testid="featured-empty">
            No premium spas listed yet. Check back soon.
          </div>
        ) : (
          <PremiumCarousel
            items={featured}
            autoplayMs={3000}
            visibleCount={6}
            testId="featured-carousel"
          />
        )}
      </section>

      {/* HOMEPAGE ADVERTISEMENT BANNER — admin-controlled, hidden entirely
          when no banner is configured or the banner is disabled. */}
      <HomeBanner />

      {/* POPULAR AREAS */}
      <section data-testid="popular-areas-section" className="section-wash">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="mb-7">
            <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Explore by area</div>
            <h2 className="font-heading text-3xl text-[#111827] mt-1">Popular Areas in Pune & PCMC</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {POPULAR_AREAS.map((a) => (
              <Link
                key={a.name}
                to={`/spas-in-${a.name.toLowerCase().replace(/\s/g, '-')}`}
                data-testid={`area-card-${a.name.toLowerCase().replace(/\s/g, '-')}`}
                className="group relative rounded-2xl overflow-hidden bg-[#F5EFE6] aspect-[5/4] card-lift"
              >
                <img src={a.image} alt={a.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(17,24,39,0.7)] via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <div className="font-heading text-lg">{a.name}</div>
                  <div className="text-xs opacity-90">Browse spas →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section data-testid="popular-categories-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-7">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">By experience</div>
          <h2 className="font-heading text-3xl text-[#111827] mt-1">Popular Categories</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-3">
          {POPULAR_CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={`/spas-by-category/${encodeURIComponent(c.name)}`}
              data-testid={`category-card-${c.name.toLowerCase().replace(/\s/g, '-')}`}
              className="flex items-center gap-3 rounded-2xl bg-[#F5EFE6] hover:bg-[#EFE7D8] border border-[rgba(17,24,39,0.06)] px-4 py-4 transition-colors"
            >
              <span className="text-2xl">{c.icon}</span>
              <div>
                <div className="font-medium text-sm text-[#111827]">{c.name}</div>
                <div className="text-[11px] text-[#4B5563]">View listings</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TOP RATED */}
      <SpaSection
        testId="top-rated-section"
        eyebrow="Loved by guests"
        title="Top Rated Spas"
        viewAllTo="/spas?sort=rating"
        items={topRated}
      />

      {/* RECENT */}
      <section data-testid="recently-added-section" className="section-wash">
        <SpaSection
          testId="recently-added-inner"
          eyebrow="Fresh on the platform"
          title="Recently Added"
          viewAllTo="/spas?sort=newest"
          items={recent}
        />
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="testimonials-section" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Real stories</div>
          <h2 className="font-heading text-3xl text-[#111827] mt-1">Loved by Pune&apos;s Wellness Seekers</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="rounded-2xl bg-white border border-[#E7DDCF] p-6">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, k) => (
                  <Star key={`${t.name}-star-${k}`} size={14} className="fill-[#C9A86A] text-[#C9A86A]" />
                ))}
              </div>
              <p className="text-sm text-[#1F2937] leading-relaxed">“{t.text}”</p>
              <div className="mt-4 pt-4 border-t border-[#E7DDCF] flex items-center justify-between text-xs">
                <span className="font-medium text-[#111827]">{t.name}</span>
                <span className="text-[#4B5563]">{t.area}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OWNER CTA */}
      <section data-testid="owner-cta-banner" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-[#1F4E3D] text-white p-8 sm:p-12 grid sm:grid-cols-12 gap-6 items-center">
          <div className="sm:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#C9A86A]/15 border border-[#C9A86A]/40 px-3 py-1.5 text-[#EDC98A] uppercase tracking-widest font-semibold text-[13px] sm:text-sm shadow-[0_0_0_1px_rgba(201,168,106,0.1)]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#EDC98A]" aria-hidden="true" />
              For Spa Owners
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl mt-2">List Your Spa. Reach Thousands.</h2>
            <p className="mt-3 text-sm sm:text-base text-white/85 max-w-xl">
              Get discovered by spa-seekers across Pune & PCMC. Free basic listing. Upgrade anytime for premium placement.
            </p>
          </div>
          <div className="sm:col-span-4 flex sm:justify-end">
            <Link to="/register" data-testid="owner-cta-list-free-button">
              <Button className="bg-[#C9A86A] hover:bg-[#D7BC86] text-[#111827] h-12 px-6 gap-2">
                List Your Spa Free <ArrowRight size={16} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
